import type { OracleFeedHealth } from './perps/oracle-health'
import { JSONContent } from '@tiptap/core'
import type { OgCardProps } from './contract-seo'
import { getDisplayProbability } from 'common/calculate'
import { Topic } from 'common/group'
import { ChartAnnotation } from 'common/supabase/chart-annotations'
import { sum } from 'lodash'
import { Answer } from './answer'
import { getLiquidity } from './calculate-cpmm'
import { MultiBase64Points } from './chart'
import { ContractComment } from './comment'
import { ContractMetric } from './contract-metric'
import { ENV_CONFIG } from './envs/constants'
import { Fees } from './fees'
import { PollOption } from './poll-option'
import { formatMoney, formatPercent } from './util/format'
import { DAY_MS } from './util/time'

/************************************************

supabase status: columns exist for
  slug: text
  creatorId: text
  question: text
  visibility: text
  mechanism: text
  outcomeType: text
  createdTime: timestamp (from millis)
  closeTime?: timestamp (from millis)
  resolutionTime?: timestamp (from millis)
  resolution?: text
  resolutionProbability?: numeric
  popularityScore: numeric
  importanceScore: numeric

any changes to the type of these columns in firestore will require modifying
the supabase trigger, or replication of contracts may fail!

*************************************************/

type AnyContractType =
  | (CPMM & Binary)
  | (CPMM & PseudoNumeric)
  | QuadraticFunding
  | (CPMM & Stonk)
  | CPMMMulti
  | (NonBet & BountiedQuestion)
  | (NonBet & Poll)
  | CPMMNumber
  | MultiNumeric
  | MultiDate
  | (PerpMechanism & Perp)
export type Contract<T extends AnyContractType = AnyContractType> = {
  id: string
  slug: string // auto-generated; must be unique

  creatorId: string
  creatorName: string
  creatorUsername: string
  creatorAvatarUrl?: string
  creatorCreatedTime?: number

  question: string
  description: string | JSONContent // More info about what the contract is about
  visibility: Visibility

  createdTime: number // Milliseconds since epoch
  lastUpdatedTime: number // Updated on any change to the market (metadata, bet, comment)
  lastBetTime?: number
  lastCommentTime?: number
  closeTime?: number // When no more trading is allowed
  deleted?: boolean // If true, don't show market anywhere.

  isResolved: boolean
  resolutionTime?: number // When the contract creator resolved the market
  resolution?: string
  resolutionProbability?: number
  resolverId?: string
  isSpicePayout?: boolean

  closeEmailsSent?: number

  volume: number
  volume24Hours: number
  elasticity: number

  collectedFees: Fees
  uniqueBettorCount: number
  uniqueBettorCountDay: number

  unlistedById?: string
  featuredLabel?: string
  isTwitchContract?: boolean

  coverImageUrl?: string
  isRanked?: boolean

  creatorBannedFromBetting?: boolean

  gptCommentSummary?: string

  token: ContractToken
  siblingContractId?: string

  /** @deprecated - no longer used */
  takerAPIOrdersDisabled?: boolean

  // Manifold.love
  loverUserId1?: string // The user id's of the pair of lovers referenced in the question.
  loverUserId2?: string // The user id's of the pair of lovers referenced in the question.
  matchCreatorId?: string // The user id of the person who proposed the match.
  isLove?: boolean

  /** @deprecated - no more auto-subsidization */
  isSubsidized?: boolean // NOTE: not backfilled, undefined = true
  /** @deprecated - try to use group-contracts table instead */
  groupSlugs?: string[]
  /** @deprecated - not deprecated, only updated in native column though*/
  popularityScore: number
  /** @deprecated - not deprecated, only updated in native column though*/
  importanceScore: number
  /** @deprecated - not deprecated, only updated in native column though*/
  homePageScoreAdjustment?: number
  /** @deprecated - not deprecated, only updated in native column though*/
  homePageScoreAdjustmentExpiresAt?: number
  /** @deprecated - not deprecated, only updated in native column though*/
  dailyScore: number
  /** @deprecated - not deprecated, only updated in native column though*/
  freshnessScore: number
  /** @deprecated - not deprecated, only updated in native column though*/
  conversionScore: number
  /** @deprecated - not deprecated, only updated in native column though*/
  viewCount: number
  /** @deprecated - not deprecated, only updated in native column though*/
  boosted: boolean
  /** @deprecated - not up-to-date */
  likedByUserCount?: number
} & T

export type ContractToken = 'MANA' | 'CASH'
export type CPMMContract = Contract & CPMM
export type CPMMMultiContract = Contract & CPMMMulti
export type CPMMNumericContract = Contract & CPMMNumber
export type MultiNumericContract = Contract & MultiNumeric
export type MultiDateContract = Contract & MultiDate
export type MarketContract =
  | CPMMContract
  | CPMMMultiContract
  | CPMMNumericContract
  | MultiNumericContract
  | MultiDateContract
export type BinaryContract = Contract & Binary
export type PseudoNumericContract = Contract & PseudoNumeric
export type QuadraticFundingContract = Contract & QuadraticFunding
export type StonkContract = Contract & Stonk
export type BountiedQuestionContract = Contract & BountiedQuestion
export type PollContract = Contract & Poll
export type SportsContract = Contract & Sports
export type BinaryOrPseudoNumericContract =
  | BinaryContract
  | PseudoNumericContract
  | StonkContract

export type CPMM = {
  mechanism: 'cpmm-1'
  pool: { [outcome: string]: number }
  p: number // probability constant in y^p * n^(1-p) = k
  totalLiquidity: number // for historical reasons, this the total subsidy amount added in Ṁ
  subsidyPool: number // current value of subsidy pool in Ṁ
  prob: number
  probChanges: {
    day: number
    week: number
    month: number
  }
}

export type NonBet = {
  mechanism: 'none'
}

export const NON_BETTING_OUTCOMES: OutcomeType[] = ['BOUNTIED_QUESTION', 'POLL']
export const NO_CLOSE_TIME_TYPES: OutcomeType[] = NON_BETTING_OUTCOMES.concat([
  'STONK',
  'PERP',
])

/**
 * Implemented as a set of cpmm-1 binary contracts, one for each answer.
 * The mechanism is stored among the contract's answers, which each
 * reference this contract id.
 *
 * Reserved cpmm-multi-2 semantics (declared now, not yet reachable): a
 * shouldAnswersSumToOne=true answer MAY in the future be individually
 * resolved NO while the market stays open (that answer's
 * `Answer.resolution` set, siblings still unresolved), with probabilities
 * summing to 1 over the *unresolved* answers. No code path creates this
 * state today — resolution of linked markets remains whole-market-only —
 * but integrators adding cpmm-multi-2 support should not assume
 * "shouldAnswersSumToOne ⇒ answers are never individually resolved".
 * (`Answer.resolution` already exists and is populated today for
 * independent markets.) cpmm-multi-1 keeps the whole-market-only
 * guarantee unchanged.
 */
export type CPMMMulti = {
  mechanism: 'cpmm-multi-1' | 'cpmm-multi-2'
  outcomeType: 'MULTIPLE_CHOICE'
  shouldAnswersSumToOne: boolean
  addAnswersMode?: add_answers_mode

  totalLiquidity: number // for historical reasons, this the total subsidy amount added in Ṁ
  subsidyPool: number // current value of subsidy pool in Ṁ
  specialLiquidityPerAnswer?: number // Special liquidity mode, where initial ante is copied into each answer's pool, with a min probability, and only one answer can resolve YES. shouldAnswersSumToOne must be false.

  // Answers chosen on resolution, with the weights of each answer.
  // Weights sum to 100 if shouldAnswersSumToOne is true. Otherwise, range from 0 to 100 for each answerId.
  resolutions?: { [answerId: string]: number }

  // What each answer opened at, by answer id, where the creator set the
  // starting probabilities. Absent for markets that opened at an even split.
  initialProbabilities?: { [answerId: string]: number }

  // NOTE: This field is stored in the answers table and must be denormalized to the client.
  answers: Answer[]
  sort?: SortType
}

export type CPMMNumber = {
  mechanism: 'cpmm-multi-1' | 'cpmm-multi-2'
  outcomeType: 'NUMBER'
  shouldAnswersSumToOne: true
  addAnswersMode: 'DISABLED'
  max: number
  min: number

  totalLiquidity: number // for historical reasons, this the total subsidy amount added in Ṁ
  subsidyPool: number // current value of subsidy pool in Ṁ

  // Answers chosen on resolution, with the weights of each answer.
  // Weights sum to 100 if shouldAnswersSumToOne is true. Otherwise, range from 0 to 100 for each answerId.
  resolutions?: { [answerId: string]: number }

  // NOTE: This field is stored in the answers table and must be denormalized to the client.
  answers: Answer[]
  sort?: SortType
}

export type add_answers_mode = 'DISABLED' | 'ONLY_CREATOR' | 'ANYONE'

export type QuadraticFunding = {
  outcomeType: 'QUADRATIC_FUNDING'
  mechanism: 'qf'
  answers: any[]
  // Mapping of how much each user has contributed to the matching pool
  // Note: Our codebase assumes every contract has a pool, which is why this isn't just a constant
  pool: { M$: number }

  // Used when the funding round pays out
  resolution?: 'MKT' | 'CANCEL'
  resolutions?: { [outcome: string]: number } // Used for MKT resolution.
}

export type Binary = {
  outcomeType: 'BINARY'
  initialProbability: number
  resolutionProbability?: number // Used for BINARY markets resolved to MKT
  resolution?: resolution
}

export type PseudoNumeric = {
  outcomeType: 'PSEUDO_NUMERIC'
  min: number
  max: number
  isLogScale: boolean
  resolutionValue?: number

  // same as binary market; map everything to probability
  initialProbability: number
  resolutionProbability?: number
}

export type Number = {
  outcomeType: 'NUMBER'
  answers: Answer[]
  min: number
  max: number
  resolution?: string | 'MKT' | 'CANCEL'
  resolutions?: { [outcome: string]: number } // Used for MKT resolution.
}

export type MultiNumeric = {
  mechanism: 'cpmm-multi-1' | 'cpmm-multi-2'
  outcomeType: 'MULTI_NUMERIC'
  unit: string
  answers: Answer[]
  shouldAnswersSumToOne: boolean
  addAnswersMode: 'DISABLED'
  totalLiquidity: number
  subsidyPool: number
  resolutions?: { [answerId: string]: number }
  resolution?: string | 'CHOOSE_MULTIPLE' | 'CANCEL'
  sort?: SortType
}

export type MultiDate = Omit<MultiNumeric, 'outcomeType' | 'unit'> & {
  outcomeType: 'DATE'
  timezone: string
  display?: 'clock'
}

export type Stonk = {
  outcomeType: 'STONK'
  initialProbability: number
}

// Perpetual futures (ManiPerp AMM). Dual liquidity pools, oracle-pegged.
// See backend/shared/src/perps/README.md and common/src/perps/ for details.
export type Perp = {
  outcomeType: 'PERP'
  maxLeverage: number // ℓ_max
  maxFundingRate: number // f_max per period
  fundingSensitivity: number // k
  maxOraclePriceAgeMs: number // block trades if feed stale
  // Taker fee in basis points of NOTIONAL, charged when opening or adding
  // (closing is free) and paid into the trader's side backing pool. Missing
  // on pre-fee contracts = PERP_TAKER_FEE_BPS_DEFAULT. Read via
  // getPerpTakerFeeBps (common/perps/fees), never directly.
  takerFeeBps?: number
  // Size-impact coefficient of the taker fee: the marginal rate at
  // pool-share s is takerFeeBps + takerFeeImpact·s² bps, integrated over the
  // added notional (see calcPerpSizeFee). Missing or 0 = flat base fee only.
  // NOT named k: the paper's k is fundingSensitivity above. Read via
  // getPerpTakerFeeImpact (common/perps/fees), never directly.
  takerFeeImpact?: number
  // Base taker fee for API-key opens, in bps of notional. Applied as
  // max(takerFeeBps, takerFeeApiBps) so it can only raise the API channel's
  // rate, never discount it. Missing = API pays the same base as the web.
  // Read via getPerpEffectiveTakerFeeBps (common/perps/fees), never directly.
  takerFeeApiBps?: number
  // ms between funding events: max(1h, feed updatePeriodMs), derived at
  // create time and frozen so later feed-registry changes can't rewrite the
  // economics of open positions. Missing on pre-period contracts = hourly.
  // Read via getFundingPeriodMs (common/perps/funding), never directly.
  fundingPeriodMs?: number
  // Short on-site identifier ("BTC", "TRUMP") shown in front of the title in
  // place of the market type, used as the row label on /perps, and matched
  // by search — which is why it is stored here rather than only mapped in
  // client code. Canonical per feed in PERP_FEED_TICKERS (common/perps/
  // ticker); stamped by create-perp, and absent on markets created before it
  // existed until backfill-perp-tickers.ts runs. Read via getPerpTicker
  // (common/perps/ticker), never directly, so those rows still get a label.
  ticker?: string
  resolution?: 'MKT' | 'CANCEL'
}

export type PerpMechanism = {
  mechanism: 'perp'
  poolLong: number // L
  poolShort: number // S
  initialSubsidy: number
  // Frozen creation-time allocation. Legacy prototypes only stored the total,
  // which cannot prove that launch backing was not structurally one-sided
  // after the live pools have moved.
  initialPoolLong?: number
  initialPoolShort?: number
  // Aggregate open notional per side, refreshed by every engine transition
  // that can change position size (open, close, funding, liquidation/ADL,
  // resolution). Denormalized purely so read paths can show the funding rate
  // without loading positions — the engine always recomputes from the
  // positions it holds under the lock and never trusts these. Absent on
  // contracts untouched since the field was added; treat as unknown, not
  // zero exposure (see getPerpFundingRate).
  openInterestLong?: number
  openInterestShort?: number
  oracleFeedId: string // free-text; matches oracle_prices.feed_id
  oraclePrice: number // last applied P
  oraclePriceTime?: number // ts of last applied P
  oracleSourceTime?: number | null // provider-declared source data as-of
  oracleFeedHealth?: OracleFeedHealth
  lastFundingTime?: number
  fundingRate?: number // last applied rate; +ve = longs pay
  resolvedOraclePrice?: number
  // Set when an oracle tick's post-transition state could not be made solvent
  // even after ADL and the cross-side deficit transfer. The tick still commits
  // the new price (a frozen mark is its own exploit surface), so the freshness
  // gate no longer protects the book — this halts trading explicitly instead.
  // Cleared by the first tick that applies cleanly, e.g. after add-perp-subsidy.
  solvencyHaltTime?: number | null
  solvencyHaltReason?: string | null
}
export type PerpContract = Contract & Perp & PerpMechanism

export type BountiedQuestion = {
  outcomeType: 'BOUNTIED_QUESTION'
  totalBounty: number
  bountyLeft: number
  /** @deprecated */
  bountyTxns?: string[]

  // Special mode where bounty pays out automatically in proportion to likes over 48 hours.
  isAutoBounty?: boolean
}

export type PollVoterVisibility = 'creator' | 'everyone'
export type PollType = 'single' | 'multi-select' | 'ranked-choice'

export type Poll = {
  outcomeType: 'POLL'
  options: PollOption[]
  resolutions?: string[]
  voterVisibility?: PollVoterVisibility
  pollType?: PollType // Default: 'single' for backwards compatibility
  maxSelections?: number // For multi-select: max options user can pick (defaults to all)
}

/**
 * moneyline, spread, total, prop: markets about a game between two teams.
 * winner, podium: markets about one session of a field event (a Grand Prix),
 * whose answers are the entrants (see common/f1-markets.ts).
 * title: a season's championship, whose answers are the drivers or teams.
 */
export type SportsMarketType =
  | 'moneyline'
  | 'spread'
  | 'total'
  | 'prop'
  | 'winner'
  | 'podium'
  | 'title'

export type Sports = {
  sportsStartTimestamp: string
  sportsEventId: string
  sportsLeague: string
  /**
   * A pipeline game market's teams, as the provider named them. The resolver
   * reads scores by these names, whichever way round a score feed lists them.
   */
  sportsHomeTeam?: string
  sportsAwayTeam?: string
  /** What the market is about the game; the sports page files it by this. */
  sportsMarketType?: SportsMarketType
  /**
   * A field event's entrants by answer index: the provider's id of the
   * driver each answer stands for, null for the stand-in answer. The
   * resolver matches results by id, never by name.
   */
  sportsEntrantIds?: (number | null)[]
  /**
   * A field event's session: when the pipeline first saw the provider mark
   * it cancelled, cleared once it's seen otherwise. N/A waits for this to be
   * old enough (common/f1-markets.ts F1_CANCEL_CONFIRM_HOURS).
   */
  sportsCanceledSince?: number
  /**
   * A field event's session: when the pipeline first saw it final, cleared
   * once it's seen otherwise. A payout waits F1_RESOLVE_DELAY_HOURS from
   * this as well as from the session's own time, so a stale or rewritten
   * date can't bring it forward.
   */
  sportsFinalSince?: number
}

export type MultiContract =
  | CPMMMultiContract
  | CPMMNumericContract
  | MultiNumericContract
  | MultiDateContract

type AnyOutcomeType =
  | Binary
  | QuadraticFunding
  | Stonk
  | BountiedQuestion
  | Poll
  | Number
  | CPMMMulti
  | PseudoNumeric
  | MultiNumeric
  | MultiDate
  | Perp

export type OutcomeType = AnyOutcomeType['outcomeType']
export type resolution = 'YES' | 'NO' | 'MKT' | 'CANCEL'
export const RESOLUTIONS = ['YES', 'NO', 'MKT', 'CANCEL'] as const
// Outcome types a user can create through create-market. PERP is deliberately
// absent: perps are created only by create-perp, which is admin-only, further
// restricted to the official Manifold account, and limited to feeds in the
// oracle registry — a perp with no data feed has nothing to price it. Listing
// it here made PERP a valid draft outcomeType even though createMarketProps
// can never accept it, so such a draft could only ever fail at submit.
export const CREATEABLE_OUTCOME_TYPES = [
  'BINARY',
  'MULTIPLE_CHOICE',
  'PSEUDO_NUMERIC',
  'STONK',
  'BOUNTIED_QUESTION',
  'POLL',
  'NUMBER',
  'MULTI_NUMERIC',
  'DATE',
] as const

export const CREATEABLE_NON_PREDICTIVE_OUTCOME_TYPES = [
  'POLL',
  'BOUNTIED_QUESTION',
]

export type CreateableOutcomeType = (typeof CREATEABLE_OUTCOME_TYPES)[number]

export const renderResolution = (resolution: string, prob?: number) => {
  return (
    {
      YES: 'YES',
      NO: 'NO',
      CANCEL: 'N/A',
      MKT: formatPercent(prob ?? 0),
    }[resolution] || resolution
  )
}

export function contractPathWithoutContract(
  creatorUsername: string,
  slug: string
) {
  return `/${creatorUsername}/${slug}`
}

export function contractUrl(contract: Contract) {
  return `https://${ENV_CONFIG.domain}${contractPath(contract)}`
}

export function contractPool(contract: Contract) {
  return contract.mechanism === 'cpmm-1'
    ? formatMoney(contract.totalLiquidity)
    : isMultiCpmm(contract)
    ? formatMoney(
        sum(
          contract.answers.map((a) =>
            getLiquidity({ YES: a.poolYes, NO: a.poolNo })
          )
        )
      )
    : 'Empty pool'
}

// True for any multi-answer CPMM market, v1 or v2. The mechanism is the AMM engine and is
// orthogonal to outcomeType, so this covers MULTIPLE_CHOICE / NUMBER / MULTI_NUMERIC / DATE
// alike (they are all cpmm-multi markets). Type guard → MultiContract.
export const isMultiCpmm = (contract: Contract): contract is MultiContract =>
  contract.mechanism === 'cpmm-multi-1' || contract.mechanism === 'cpmm-multi-2'

// For sites that only have the mechanism string (destructured, raw, or compound `|| 'cpmm-1'`).
export const isMultiCpmmMechanism = (mechanism: string): boolean =>
  mechanism === 'cpmm-multi-1' || mechanism === 'cpmm-multi-2'

// Raw-SQL fragment for the same predicate (the TS helpers can't reach SQL string literals).
export const MULTI_CPMM_MECHANISMS_SQL = `('cpmm-multi-1', 'cpmm-multi-2')`

export const isBinaryMulti = (contract: Contract) =>
  isMultiCpmm(contract) &&
  contract.outcomeType !== 'NUMBER' &&
  contract.outcomeType !== 'MULTI_NUMERIC' &&
  contract.outcomeType !== 'DATE' &&
  contract.answers.length === 2 &&
  contract.addAnswersMode === 'DISABLED' &&
  contract.shouldAnswersSumToOne
// contract.createdTime > 1708574059795 // In case we don't want to convert pre-commit contracts

export const isSportsContract = (
  contract: Contract
): contract is SportsContract => 'sportsEventId' in contract

/**
 * The main (first) answer of a versus market, which the UI treats as the YES
 * side. Bets can be stored against either answer; use `versusSide` in
 * common/versus to work out which side a bet, order or position backs.
 */
export const getMainBinaryMCAnswer = (contract: Contract) =>
  isBinaryMulti(contract) && isMultiCpmm(contract)
    ? contract.answers[0]
    : undefined

/**
 * Probability of the side a bet backs, given a price quoted for the answer
 * the bet was placed on. Same as `versusSideProb` in common/versus.
 */
export const getBinaryMCProb = (prob: number, outcome: 'YES' | 'NO' | string) =>
  outcome === 'YES' ? prob : 1 - prob

export function getBinaryProbPercent(contract: BinaryContract) {
  return formatPercent(getDisplayProbability(contract))
}

export function tradingAllowed(contract: Contract, answer?: Answer) {
  return (
    !contract.isResolved &&
    (!contract.closeTime || contract.closeTime > Date.now()) &&
    contract.mechanism !== 'none' &&
    (!answer || !answer.resolution)
  )
}

export const MAX_QUESTION_LENGTH = 120
export const MAX_DESCRIPTION_LENGTH = 16000

export const CPMM_MIN_POOL_QTY = 0.01
export const NUMBER_BUCKETS_MAX = 50
export const NUMBER_CREATION_ENABLED = false
// cpmm-multi-2 kill-switch: with it on, every new multiple choice market opens
// as cpmm-multi-2 (opensAsCpmmMulti2), with or without starting probabilities;
// numeric and date markets stay cpmm-multi-1. Reads are always safe (p ?? 0.5),
// so only the creation path is flagged. Turning it off makes new markets
// cpmm-multi-1 again; markets already open keep the mechanism they have.
export const CPMM_MULTI_2_CREATION_ENABLED = true
// Separately gates converting an existing cpmm-multi-1 market to cpmm-multi-2
// the first time a user adds liquidity to it. Unlike creation, that changes how
// a live market trades (limit-order fills, liquidity adds) under positions and
// orders placed against v1, so it stays off until v2 has run on new markets.
export const CPMM_MULTI_2_CONVERSION_ENABLED = false
// Whether adding liquidity converts this market to cpmm-multi-2. Only multiple
// choice markets: numeric and date markets take bets across several answers at
// once (multi-bet), which cpmm-multi-2 refuses for now.
export const convertsToCpmmMulti2 = (contract: Contract) =>
  CPMM_MULTI_2_CONVERSION_ENABLED &&
  contract.mechanism === 'cpmm-multi-1' &&
  contract.outcomeType === 'MULTIPLE_CHOICE'

export type Visibility = 'public' | 'unlisted'
export const VISIBILITIES = ['public', 'unlisted'] as const

export const SORTS = [
  { label: 'High %', value: 'prob-desc' },
  { label: 'Mid %', value: 'prob-mid' },
  { label: 'Low %', value: 'prob-asc' },
  { label: 'Oldest', value: 'old' },
  { label: 'Newest', value: 'new' },
  { label: 'Trending', value: 'liquidity' },
  { label: 'A-Z', value: 'alphabetical' },
] as const

export type SortType = (typeof SORTS)[number]['value']

export const MINUTES_ALLOWED_TO_UNRESOLVE = 10

export function contractPath(contract: {
  creatorUsername: string
  slug: string
}) {
  return `/${contract.creatorUsername}/${contract.slug}`
}

export type CashType = {
  contract: Contract
  lastBetTime?: number
  pointsString: string
  multiPointsString: MultiBase64Points
  totalPositions: number
  totalBets: number
}

export type ContractParams = {
  contract: Contract
  lastBetTime?: number
  pointsString?: string
  /** Social preview card props, built before answers are truncated for the page */
  ogCardProps?: OgCardProps
  multiPointsString?: MultiBase64Points
  comments: ContractComment[]
  totalComments: number
  totalPositions: number
  totalBets: number
  /** How many answers the market has. A big market's contract holds only its top answers until the live contract loads */
  totalAnswers?: number
  topContractMetrics: ContractMetric[]
  relatedContracts: Contract[]
  chartAnnotations: ChartAnnotation[]
  topics: Topic[]
  dashboards: { slug: string; title: string }[]
  pinnedComments: ContractComment[]
}

export type MaybeAuthedContractParams =
  | {
      state: 'authed'
      params: ContractParams
    }
  | {
      state: 'deleted'
    }

export const MAX_CPMM_PROB = 0.99
export const MIN_CPMM_PROB = 0.01
export const MAX_STONK_PROB = 0.95
export const MIN_STONK_PROB = 0.2

export const isMarketRanked = (contract: Contract) =>
  contract.isRanked != false &&
  contract.visibility === 'public' &&
  contract.deleted !== true

export const PROFIT_CUTOFF_TIME = 1715805887741
export const DPM_CUTOFF_TIMESTAMP = '2023-08-01 18:06:58.813000 +00:00'
export const getAdjustedProfit = (
  contract: Contract,
  profit: number,
  answers: Answer[] | undefined,
  answerId: string | null
) => {
  if (isMultiCpmm(contract)) {
    // Null answerId stands for the summary of all answer metrics
    if (!answerId) {
      return isMarketRanked(contract) &&
        contract.resolutionTime &&
        contract.resolutionTime <= PROFIT_CUTOFF_TIME &&
        contract.createdTime > Date.parse(DPM_CUTOFF_TIMESTAMP)
        ? 9 * profit
        : isMarketRanked(contract)
        ? undefined
        : -1 * profit
    }
    const answer = answers?.find((a) => a.id === answerId)
    if (!answer) {
      console.log(
        `answer with id ${answerId} not found, but is required for cpmm-multi-1 contract: ${contract.id}`
      )
      return undefined
    }
    return isMarketRanked(contract) &&
      answer.resolutionTime &&
      answer.resolutionTime <= PROFIT_CUTOFF_TIME &&
      contract.createdTime > Date.parse(DPM_CUTOFF_TIMESTAMP)
      ? 9 * profit
      : isMarketRanked(contract)
      ? undefined
      : -1 * profit
  }

  return isMarketRanked(contract) &&
    contract.resolutionTime &&
    contract.resolutionTime <= PROFIT_CUTOFF_TIME
    ? 9 * profit
    : isMarketRanked(contract)
    ? undefined
    : -1 * profit
}

// Add the AIGeneratedMarket type definition here, before the API object
export type AIGeneratedMarket = Pick<
  MarketContract,
  'question' | 'description'
> & {
  outcomeType:
    | 'INDEPENDENT_MULTIPLE_CHOICE'
    | 'DEPENDENT_MULTIPLE_CHOICE'
    | 'BINARY'
    | 'POLL'
  descriptionMarkdown: string
  closeDate: string
  shouldAnswersSumToOne?: boolean
  initialProb?: number
  answers?: string[]
  reasoning?: string
  addAnswersMode?: add_answers_mode
  promptVersion: number
}

export const nativeContractColumnsArray = [
  'data',
  'importance_score',
  'home_page_score_adjustment',
  'home_page_score_adjustment_expires_at',
  'freshness_score',
  'conversion_score',
  'view_count',
  'token',
  'boosted',
  'daily_score',
]

export const clampChange = (currentProb: number, probChange: number) => {
  if (probChange < 0.01 && probChange > -0.01) return 0

  if (probChange > 0) {
    // For positive changes, clamp to min of change and current probability
    return Math.min(probChange, currentProb)
  } else {
    // For negative changes, clamp to min of absolute change and (1 - currentProb)
    return -Math.min(Math.abs(probChange), 1 - currentProb)
  }
}

export const dayProbChange = (contract: CPMMContract) => {
  const { createdTime } = contract
  if (Date.now() - createdTime < DAY_MS) {
    return 0
  }
  const change = Math.abs(
    Math.round(clampChange(contract.prob, contract.probChanges?.day ?? 0) * 100)
  )
  return change > 2 ? change : 0
}
